import { useEffect, useState } from "react";
import { OfflineBanner } from "./components/OfflineBanner";
import { SecurityCenter } from "./components/SecurityCenter";
import type { SecurityCenterTab } from "./components/SecurityCenter";
import { SecurityUpgradePrompt } from "./components/SecurityUpgradePrompt";
import { useEntitlements } from "./hooks/useEntitlements";
import { useHostConvexTokenHeartbeat } from "./hooks/useHostConvexTokenHeartbeat";
import { WorkbenchView } from "./components/WorkbenchView";
import { DownloadView } from "./components/DownloadView";
import { applyTheme, postThemeToNative } from "./theme";
import { useNotifications } from "./services/notifications";
import { chatsStore } from "./stores/chatsStore";
import { settingsStore } from "./stores/settingsStore";
import { consumeDownloadIntent } from "./funnel/intent";
import { hideNative } from "./services/nativeBrowser";

type RouteKind = "workbench" | "security-center" | "download";

/** Derive the initial SecurityCenter tab from the URL path / query string. */
function tabForPath(pathname: string, search: string): SecurityCenterTab {
  // ?tab=manage or the back-compat /admin path both open the Manage tab.
  if (pathname.startsWith("/admin")) return "manage";
  const params = new URLSearchParams(search);
  const tab = params.get("tab");
  if (tab === "manage") return "manage";
  return "monitor";
}

function routeForPath(pathname: string): RouteKind {
  // /security-room, /security-center, and /admin all map to the Security Center.
  if (
    pathname.startsWith("/security-room") ||
    pathname.startsWith("/security-center") ||
    pathname.startsWith("/admin")
  ) {
    return "security-center";
  }
  if (pathname.startsWith("/download")) return "download";
  return "workbench";
}

export function App() {
  const initialRoute = routeForPath(window.location.pathname);
  const [route, setRoute] = useState<RouteKind>(() => initialRoute);
  // The initial SecurityCenter tab is derived from the URL and held as stable
  // state so it survives popstate navigation while the component is mounted.
  const [securityCenterTab, setSecurityCenterTab] = useState<SecurityCenterTab>(() =>
    tabForPath(window.location.pathname, window.location.search)
  );
  // Track every route the user has visited this session. Once a view
  // is mounted it stays mounted (just gets [hidden] applied when
  // inactive), so terminals/chats/composer state never get unmounted.
  // Initial visited set contains only the current route so users who
  // never click Security Center don't pay its mount cost.
  const [visited, setVisited] = useState<Set<RouteKind>>(() => new Set([initialRoute]));
  // Update synchronously during render so the route mounts on the SAME render
  // it becomes active. A useEffect here would leave one frame where `route`
  // changed but `visited` didn't yet contain it, producing a blank flash on
  // first navigation to a route. React's setState-during-render pattern
  // discards this render's JSX and re-renders with the new state before
  // committing to the DOM, so no flicker.
  if (!visited.has(route)) {
    setVisited((prev) => (prev.has(route) ? prev : new Set(prev).add(route)));
  }

  useEffect(() => {
    const onPopState = () => {
      setRoute(routeForPath(window.location.pathname));
      setSecurityCenterTab(tabForPath(window.location.pathname, window.location.search));
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Consuming on mount is sufficient: setDownloadIntent() is only called from
  // PricingPage, which PlanGate renders exclusively for no-plan orgs — i.e. while
  // App is unmounted. After the webhook grants entitlement, App mounts fresh here
  // and consumes the intent. (Existing-subscriber tier changes go through the admin
  // console, not PricingPage, so they never set this intent.)
  useEffect(() => {
    if (consumeDownloadIntent()) {
      window.history.pushState({}, "", "/download");
      setRoute("download");
    }
  }, []);

  // Notifications subscription is now Convex-reactive — useNotifications()
  // wires useQuery(api.notifications.queries.inbox) and detects new rows
  // to fire notifyService.show() for chime + Tauri OS notification. No
  // more SSE EventSource; the Convex client multiplexes the WebSocket
  // shared with every other useQuery so we don't open a separate stream.
  //
  // subscribe:true designates THIS hook instance as the side-effect
  // owner. Every other useNotifications() call (e.g. NotificationToggle
  // in the topbar) must omit this flag so chimes don't fire twice
  // (Codex PR #55 C55-2).
  useNotifications({ subscribe: true });

  // The host's later Convex writes for this user (terminal snapshots, close
  // records, notifications, audit events, chat replies) need a current token,
  // and it can only get one from us.
  useHostConvexTokenHeartbeat();

  useEffect(() => {
    function onNotification(e: Event) {
      const detail = (e as CustomEvent).detail;
      if (detail?.source?.type === "chat") {
        chatsStore.markUnread(detail.source.id);
      }
    }
    window.addEventListener("agentguard:notification", onNotification);
    return () => window.removeEventListener("agentguard:notification", onNotification);
  }, []);

  useEffect(() => {
    // Pre-settings-load: paint immediately from localStorage cache to avoid
    // a flash of unstyled content. The effect below takes over once
    // settingsStore.loaded flips true and applies the server-persisted theme.
    applyTheme(localStorage.getItem("agentguard-theme") || "dark");
    postThemeToNative(document.documentElement.dataset.theme || "dark");
    const onNativeTheme = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail;
      if (detail === "dark" || detail === "light") applyTheme(detail);
    };
    window.addEventListener("agentguard-native-theme", onNativeTheme as EventListener);
    return () =>
      window.removeEventListener("agentguard-native-theme", onNativeTheme as EventListener);
  }, []);

  // Server-persisted appearance is the source of truth once /api/settings has loaded.
  // Without this, a cleared localStorage / fresh browser profile / "system" choice
  // would keep falling back to "dark" until the user re-opened the Appearance panel.
  const settingsState = settingsStore.useStore();
  useEffect(() => {
    if (!settingsState.loaded) return;
    applyTheme(settingsState.settings.appearance.theme);
    postThemeToNative(document.documentElement.dataset.theme || "dark");
  }, [settingsState.loaded, settingsState.settings.appearance.theme]);

  useEffect(() => {
    if (route !== "workbench") void hideNative();
  }, [route]);

  return (
    <div className="react-route-root">
      <OfflineBanner />
      {visited.has("workbench") && (
        <div hidden={route !== "workbench"} className="route-pane">
          <WorkbenchView active={route === "workbench"} />
        </div>
      )}
      {visited.has("security-center") && (
        <div hidden={route !== "security-center"} className="route-pane">
          <SecurityCenterRoute initialTab={securityCenterTab} />
        </div>
      )}
      {visited.has("download") && (
        <div hidden={route !== "download"} className="route-pane">
          <DownloadView />
        </div>
      )}
    </div>
  );
}

/**
 * Route gate for the Security Center.
 *
 * Tier model is cumulative (base ⊂ pro ⊂ security). The Security Center route
 * hosts BOTH the Monitor (SecurityRoomView — security-only) AND the Manage tab
 * (AdminConsoleView — which is the ONLY place to manage billing + the Pro
 * GitHub App + audit). So the ROUTE itself must be reachable by ANY entitled
 * subscriber; the per-tab/per-feature gating happens INSIDE SecurityCenter and
 * AdminConsoleView (which skip security-gated queries for non-security users so
 * they never 402-crash a pane).
 *
 * Loading → null (so a security user never flashes the upgrade prompt). The
 * upgrade fallback is purely defensive: PlanGate upstream already renders the
 * PricingPage for fully-unentitled (no-plan) orgs, so a "ready" state here
 * always carries at least the base tier (hasBase === true).
 */
function SecurityCenterRoute({ initialTab }: { initialTab: SecurityCenterTab }) {
  const ent = useEntitlements();
  if (ent.kind === "loading") return null;
  if (ent.kind !== "ready" || !ent.hasBase) return <SecurityUpgradePrompt />;
  return <SecurityCenter initialTab={initialTab} />;
}
